import { useEffect, useRef, useState } from 'react';
import { ScreenNamesEnum } from '@/constants';
import { useAppRouter } from '@/hooks/use.app.router';
import { fontSize } from '@/hooks/use.screen.size';
import { Box } from '@/components/base/box';
import { AppTextView } from '@/components/base/app.text.view';
import { AppButton } from '@/components/base/app.button';
import { AppModal } from '@/components/compose/app.modal';
import OTPInputView from '@/components/compose/otp.input';
import { useFcmToken } from '@/hooks/use.fcm.token';
import { Platform } from 'react-native';
import styled from 'styled-components/native';
import { getAppVersion } from '../../utils';
import { useSendLoginOTP } from '@/apis/auth';
import { useNavigation } from '@react-navigation/native';
import { getModalCallback } from '@/navigation/modal.callbacks';

const DIGITS = 5;

export default function OTPModal() {
  const navigation = useNavigation();
  const { params, resetStackTo } = useAppRouter();
  const mobile = (params as any).mobile;
  const onResend = getModalCallback('otp:onResend');
  const [code, setCode] = useState<string>('');
  const [seconds, setSeconds] = useState(60);
  const [clearInputs, setClearInputs] = useState(false);
  const { fcmToken } = useFcmToken();
  const ref = useRef<OTPInputView>(null);

  useEffect(() => {
    const id = setTimeout(() => ref.current?.focusField(0), 500);
    return () => clearTimeout(id);
  }, []);

  // 2. Add the `if (clearInputs)` guard to prevent this running on initial mount
  useEffect(() => {
    if (clearInputs) {
      ref.current?.clearAllFields(true);
      ref.current?.focusField(0);
      const id = setTimeout(() => setClearInputs(false), 500);
      return () => clearTimeout(id);
    }
  }, [clearInputs]);

  const { mutate: sendOtp } = useSendLoginOTP({
    onSuccess: () => {
      navigation.goBack();
      resetStackTo(ScreenNamesEnum.MainScreen);
    },
    onError: () => {
      setClearInputs(true);
    },
  });

  useEffect(() => {
    if (seconds <= 0) return;
    const id = setTimeout(() => {
      setSeconds(seconds - 1);
    }, 1000);
    return () => clearTimeout(id);
  }, [seconds]);

  useEffect(() => {
    if (code.length === DIGITS) {
      sendOtp({ body: { otp: code, fcmToken, os: Platform.OS, version: getAppVersion() } });
    }
  }, [code, fcmToken, sendOtp]);

  return (
    <AppModal title={'ورود کد فعالسازی'}>
      <ContentBox>
        <DescriptionText variant={'primary'} textAlign={'center'} textColor={'grey'} text={'کد 5 رقمی ارسال شده به شماره همراه خود را وارد کنید'} />

        <ChangeNumberButton fullWidth={false} title={`تغییر شماره ${mobile}`} mode={'text'} onPress={() => navigation.goBack()} />
        <StyledOTPInput
          ref={ref}
          autoFocusOnLoad={false}
          clearInputs={clearInputs}
          pinCount={DIGITS}
          onCodeChanged={(code) => {
            setCode(code);
          }}
        />
        <AutoWidthBox>
          <ResendButton
            title={'ارسال مجدد'}
            disabled={seconds !== 0}
            onPress={() => {
              if (seconds > 0) {
                return;
              }
              onResend();
              setSeconds(60);
            }}
          />
        </AutoWidthBox>
        {seconds !== 0 && <TimerText variant={'medium'} text={`${String(seconds)}  ثانیه تا ارسال مجدد کد`} textColor="jade" />}
      </ContentBox>
    </AppModal>
  );
}

const ContentBox = styled(Box)`
  margin-top: 55px;
  align-self: center;
`;

const DescriptionText = styled(AppTextView)`
  align-self: flex-end;
  margin-top: 12px;
`;

const ChangeNumberButton = styled(AppButton)`
  margin-top: 30px;
  align-self: flex-end;
`;

const StyledOTPInput = styled(OTPInputView).attrs(({ theme }) => ({
  codeInputFieldStyle: {
    fontFamily: theme.fonts.primary,
    fontSize: fontSize(15),
    marginHorizontal: theme.wp(0.5),
    color: theme.colors.blue,
    borderColor: theme.colors.jadeLight,
    borderWidth: 2,
    borderRadius: 10,
  },
}))`
  align-self: center;
  margin-top: 20px;
  height: 100px;
  max-width: ${({ theme }) => theme.wp(80)}px;
`;

const AutoWidthBox = styled(Box)`
  width: auto;
`;

const ResendButton = styled(AppButton)`
  margin-top: 20px;
`;

const TimerText = styled(AppTextView)`
  align-self: center;
  margin-top: ${({ theme }) => theme.hp(3)}px;
`;
